import { isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  CONDITION_OPERATOR_OPTIONS,
  applyLimitDerivedFields,
  conditionFingerprint,
  conditionValues,
  decimalPlaces,
  findConflictingLimitRule,
  findDuplicateLimitRule,
  findOverlappingLimitRules,
  formatConditionLine,
  formatLimitDisplay,
  getAvailableConditionFields,
  getCompatibleLimitTypes,
  getCompatibleOperators,
  getCompatibleUnits,
  getConditionValueOptions,
  getLimitForDefinition,
  getLimitForLabel,
  getLimitForOptions,
  getLimitTypeLabel,
  getMeasureLabel,
  getOperatorLabel,
  getPurposeMeaning,
  getPurposeReference,
  getRequirementLabel,
  getUnitLabel,
  integersOnlyFor,
  namesMatch,
  nextLimitRuleId,
  normalizeLimitForm,
  operatorIsMulti,
  parseEnteredNumber,
  rangeHasPermittedValue,
  recordTypeModule,
  buildLimitScopeKey,
} from './limitsThresholdsConfig';
import { emitConfigurationHistoryBatch } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  team: 'Platform Operations',
  accountId: 'ADM-0002',
};

const TRACKED_FIELDS = [
  'ruleName',
  'description',
  'requirement',
  'limitFor',
  'limitType',
  'operator',
  'value',
  'minimum',
  'maximum',
  'minimumBoundary',
  'maximumBoundary',
  'unit',
  'conditions',
];

export function limitRuleActor() {
  return { ...ACTOR };
}

export function canAccessLimitsConfiguration(auth) {
  return isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);
}

export function canViewLimitsConfiguration(auth) {
  return canAccessLimitsConfiguration(auth);
}

export function canAddLimitRule(auth) {
  return canAccessLimitsConfiguration(auth);
}

export function canEditLimitRule(auth) {
  return canAccessLimitsConfiguration(auth);
}

export function canSaveLimitRule(auth) {
  return canAccessLimitsConfiguration(auth);
}

function addError(result, key, message) {
  if (!result.summary.includes(message)) result.summary.push(message);
  if (key && !result.errors[key]) result.errors[key] = message;
}

function isBlank(value) {
  return value == null || !String(value).trim();
}

function conditionsCannotAllBeTrue(conditions = []) {
  const byField = {};
  conditions.forEach((condition) => {
    if (!condition.field || !condition.operator) return;
    const values = conditionValues(condition);
    if (!values.length) return;
    if (!byField[condition.field]) byField[condition.field] = [];
    byField[condition.field].push({ operator: condition.operator, values });
  });

  const impossibleSameField = Object.values(byField).some((entries) => {
    const positives = [...new Set(entries.filter((entry) => ['is', 'is_any_of'].includes(entry.operator)).flatMap((entry) => entry.values))];
    const exclusivePositives = entries.filter((entry) => entry.operator === 'is' || (entry.operator === 'is_any_of' && entry.values.length === 1));
    if (exclusivePositives.length > 1) {
      const values = exclusivePositives.map((entry) => entry.values[0]);
      if (new Set(values).size > 1) return true;
    }
    return entries.some((entry) => {
      if (['is_not', 'is_not_any_of'].includes(entry.operator) && positives.length && positives.every((value) => entry.values.includes(value))) return true;
      return false;
    });
  });

  const moduleValues = [];
  const recordTypeValues = [];
  conditions.forEach((condition) => {
    if (condition.field === 'module' && ['is', 'is_any_of'].includes(condition.operator)) moduleValues.push(...conditionValues(condition));
    if (condition.field === 'record_type') recordTypeValues.push(...conditionValues(condition));
  });
  const associationInvalid = recordTypeValues.some((value) => {
    const moduleFromType = recordTypeModule(value);
    return moduleValues.length && !moduleValues.includes(moduleFromType);
  });

  return impossibleSameField || associationInvalid;
}

function validateEnteredNumber(result, key, raw, purpose, unit) {
  const fieldLabel = key === 'value' ? 'Value' : key === 'minimum' ? 'Minimum' : 'Maximum';
  const parsed = parseEnteredNumber(raw);
  if (parsed.empty) {
    addError(result, key, `Enter a valid number for ${fieldLabel}.`);
    return null;
  }
  if (!parsed.finite) {
    addError(result, key, `Enter a valid number for ${fieldLabel}.`);
    return null;
  }
  const integersOnly = integersOnlyFor(purpose, unit);
  if (integersOnly && decimalPlaces(parsed.text) > 0) {
    addError(result, key, unit === 'calendar_days' ? 'Enter a whole number of calendar days.' : 'Enter a whole number.');
    return null;
  }
  const maxDecimals = unit === 'bytes' ? 0 : (purpose?.maxDecimals ?? 0);
  if (!integersOnly && decimalPlaces(parsed.text) > maxDecimals) {
    addError(result, key, `Use up to ${maxDecimals} decimal places for this Unit.`);
    return null;
  }
  if (purpose?.max === 100 && (parsed.number < 0 || parsed.number > 100)) {
    addError(result, key, 'Percentage must be between 0 and 100.');
    return null;
  }
  if (purpose?.exclusiveMin && parsed.number <= (purpose.min ?? 0)) {
    addError(result, key, 'This value must be greater than zero.');
    return null;
  }
  if (!purpose?.exclusiveMin && purpose?.min != null && parsed.number < purpose.min) {
    addError(result, key, `Enter a number of zero or more for ${fieldLabel}.`);
    return null;
  }
  if (purpose?.max != null && purpose.max !== 100 && parsed.number > purpose.max) {
    addError(result, key, 'This restriction contains no permitted value.');
    return null;
  }
  return parsed.number;
}

export function collectLimitSaveErrors(formData, allRules, excludeId, options = {}) {
  const form = applyLimitDerivedFields(normalizeLimitForm(formData));
  const result = { errors: {}, summary: [] };
  const others = (allRules || []).filter((rule) => rule.id !== excludeId).map(normalizeLimitForm);

  if (options.canSave === false) {
    addError(result, 'permission', 'You do not have permission to save this Rule.');
    return result;
  }
  if (options.checksAvailable === false) {
    addError(result, 'checksUnavailable', 'A required compatibility check could not be completed.');
    return result;
  }

  if (isBlank(form.ruleName)) addError(result, 'ruleName', 'Enter a Rule Name.');
  else if (others.some((rule) => rule.requirement === form.requirement && rule.limitFor === form.limitFor && namesMatch(rule.ruleName, form.ruleName))) {
    addError(result, 'ruleName', 'This Rule Name is already used for this Requirement and Limit For.');
  }
  if (isBlank(form.description)) addError(result, 'description', 'Enter a Description.');

  if (!form.requirement) addError(result, 'requirement', 'Select Requirement.');
  const purposeOptions = getLimitForOptions(form.requirement);
  if (!form.limitFor) {
    addError(result, 'limitFor', purposeOptions.length ? 'Select Limit For.' : 'Required choices for this purpose are not fully defined.');
  } else if (!purposeOptions.some((option) => option.value === form.limitFor)) {
    addError(result, 'limitFor', 'This selection is not supported for the selected purpose.');
  }

  const purpose = getLimitForDefinition(form.limitFor);
  if (form.limitFor && (!purpose || purpose.available === false || !purpose.meaning || !purpose.reference)) {
    addError(result, 'limitFor', 'Required choices for this purpose are not fully defined.');
  }

  if (excludeId && options.originalScopeKey) {
    const currentKey = `${form.requirement}|${form.limitFor}`;
    if (currentKey !== options.originalScopeKey) {
      addError(result, 'scope', 'Requirement and Limit For cannot be changed after Save.');
    }
  }

  const derivedMeasure = applyLimitDerivedFields({ ...form }).measure;
  if (form.measure && derivedMeasure && form.measure !== derivedMeasure) {
    addError(result, 'measure', 'Measure must match the Requirement.');
  }

  const typeOptions = getCompatibleLimitTypes(form.limitFor);
  if (!form.limitType) addError(result, 'limitType', 'Select Limit Type.');
  else if (form.limitFor && !typeOptions.some((option) => option.value === form.limitType)) {
    addError(result, 'limitType', 'This selection is not supported for the selected purpose.');
  }

  const operatorOptions = getCompatibleOperators(form.limitFor, form.limitType);
  if (!form.operator) addError(result, 'operator', 'Select Operator.');
  else if (form.limitType && !operatorOptions.some((option) => option.value === form.operator)) {
    addError(result, 'operator', 'This selection is not supported for the selected purpose.');
  }

  const unitOptions = getCompatibleUnits(form.limitFor);
  if (!form.unit) addError(result, 'unit', 'Select Unit.');
  else if (form.limitFor && !unitOptions.some((option) => option.value === form.unit)) {
    addError(result, 'unit', 'This selection is not supported for the selected purpose.');
  }

  if (purpose?.units?.includes('calendar_days') && form.unit && form.unit !== 'calendar_days') {
    addError(result, 'unit', 'Select Calendar Days for this purpose.');
  }

  if (purpose?.fixedUnit && form.unit && form.unit !== purpose.fixedUnit) {
    addError(result, 'unit', 'This value does not meet the requirements for the selected purpose.');
  }
  if (purpose?.fixedValue != null && String(form.value) !== String(purpose.fixedValue)) {
    addError(result, 'value', 'This value does not meet the requirements for the selected purpose.');
  }

  if (form.limitType === 'single_value') {
    const number = validateEnteredNumber(result, 'value', form.value, purpose, form.unit);
    if (number != null && purpose?.max === 100 && form.operator === 'greater_than' && number >= 100) {
      addError(result, 'value', 'This restriction contains no permitted value.');
    }
    if (number != null && purpose?.max === 100 && form.operator === 'less_than' && number <= 0) {
      addError(result, 'value', 'This restriction contains no permitted value.');
    }
  }

  if (form.limitType === 'min_max_range') {
    const min = validateEnteredNumber(result, 'minimum', form.minimum, purpose, form.unit);
    const max = validateEnteredNumber(result, 'maximum', form.maximum, purpose, form.unit);
    if (!form.minimumBoundary) addError(result, 'minimumBoundary', 'Select Minimum Boundary.');
    if (!form.maximumBoundary) addError(result, 'maximumBoundary', 'Select Maximum Boundary.');
    if ((form.minimum === '' || form.maximum === '' || !form.minimumBoundary || !form.maximumBoundary)
      && !result.errors.minimum && !result.errors.maximum) {
      addError(result, 'minimum', 'Enter both limits and select both boundaries.');
    }
    if (min != null && max != null) {
      if (min > max) addError(result, 'minimum', 'Minimum must not exceed Maximum.');
      else if (min === max && (form.minimumBoundary !== 'included' || form.maximumBoundary !== 'included')) {
        addError(result, 'minimumBoundary', 'Equal boundaries must both be Included.');
      } else if (!rangeHasPermittedValue(min, max, form.minimumBoundary, form.maximumBoundary, integersOnlyFor(purpose, form.unit))) {
        addError(result, 'minimum', 'This restriction contains no permitted value.');
      }
    }
  }

  if (isBlank(form.changeReason)) addError(result, 'changeReason', 'Enter a Change Reason.');

  const availableFields = getAvailableConditionFields(form.limitFor).map((option) => option.value);
  const seenFingerprints = [];
  (form.conditions || []).forEach((condition, index) => {
    const key = `condition_${index}`;
    const values = conditionValues(condition);
    if (!condition.field || !condition.operator || !values.length) {
      addError(result, key, 'Complete or remove this Condition.');
      return;
    }
    if (!availableFields.includes(condition.field)) {
      addError(result, key, 'This Condition is not supported for the selected purpose.');
    }
    if (!CONDITION_OPERATOR_OPTIONS.some((option) => option.value === condition.operator)) {
      addError(result, key, 'This Field, Operator or Value is incompatible.');
    }
    if (!operatorIsMulti(condition.operator) && values.length !== 1) {
      addError(result, key, 'Complete or remove this Condition.');
    }
    if (values.length !== new Set(values).size) {
      addError(result, key, 'This value or Condition is already included.');
    }
    const valueOptions = getConditionValueOptions(condition.field, form.conditions).map((option) => option.value);
    if (!valueOptions.length) {
      addError(result, key, 'No compatible values available.');
    } else if (values.some((value) => !valueOptions.includes(value))) {
      addError(result, key, 'The selected Value is unavailable.');
    }
    if (condition.field === 'record_type') {
      const modules = (form.conditions || [])
        .filter((item) => item.field === 'module' && ['is', 'is_any_of'].includes(item.operator))
        .flatMap(conditionValues);
      if (modules.length && values.some((value) => !modules.includes(recordTypeModule(value)))) {
        addError(result, key, 'These Conditions cannot match together.');
      }
    }
    const fingerprint = conditionFingerprint(condition);
    if (seenFingerprints.includes(fingerprint)) addError(result, key, 'This value or Condition is already included.');
    seenFingerprints.push(fingerprint);
  });

  if (conditionsCannotAllBeTrue(form.conditions)) {
    addError(result, 'conditions', 'These Conditions cannot match together.');
  }

  const duplicate = findDuplicateLimitRule(others, form);
  if (duplicate) addError(result, 'duplicate', `This Rule duplicates ${duplicate.id}.`);

  const overlaps = findOverlappingLimitRules(others, form);
  const conflict = findConflictingLimitRule(others, form);
  if (conflict) {
    addError(result, 'overlap', `This Rule overlaps ${conflict.id}.`);
  } else if (overlaps.length) {
    addError(result, 'overlap', `This Rule overlaps ${overlaps.map((rule) => rule.id).join(' and ')}.`);
  }

  if (excludeId && options.originalLastUpdated) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    if (current && (current.lastUpdated || current.modifiedOn) && (current.lastUpdated || current.modifiedOn) !== options.originalLastUpdated) {
      addError(result, 'concurrent', 'This Rule has changed since you opened it. Review the latest saved Rule.');
    }
  }

  return result;
}

export function ruleSnapshotFromForm(formData) {
  const form = applyLimitDerivedFields(normalizeLimitForm(formData));
  return {
    ruleName: String(form.ruleName || '').trim(),
    description: String(form.description || '').trim(),
    requirement: form.requirement,
    limitFor: form.limitFor,
    measure: form.measure,
    limitType: form.limitType,
    operator: form.operator,
    value: form.limitType === 'single_value' ? String(form.value || '').trim() : '',
    minimum: form.limitType === 'min_max_range' ? String(form.minimum || '').trim() : '',
    maximum: form.limitType === 'min_max_range' ? String(form.maximum || '').trim() : '',
    minimumBoundary: form.limitType === 'min_max_range' ? form.minimumBoundary : '',
    maximumBoundary: form.limitType === 'min_max_range' ? form.maximumBoundary : '',
    unit: form.unit,
    conditions: (form.conditions || []).map((condition) => ({
      id: condition.id,
      field: condition.field,
      operator: condition.operator,
      values: conditionValues(condition),
    })),
    changeReason: String(form.changeReason || '').trim(),
  };
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeLimitForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizeLimitForm({ ...rule, pendingDraft: null });
}

function fieldLabel(key) {
  const labels = {
    ruleName: 'Rule Name',
    description: 'Description',
    requirement: 'Requirement',
    limitFor: 'Limit For',
    limitType: 'Limit Type',
    operator: 'Operator',
    value: 'Value',
    minimum: 'Minimum',
    maximum: 'Maximum',
    minimumBoundary: 'Minimum Boundary',
    maximumBoundary: 'Maximum Boundary',
    unit: 'Unit',
    conditions: 'Conditions',
  };
  return labels[key] || key;
}

function displayFieldValue(key, value) {
  if (key === 'conditions') {
    if (!value?.length) return 'No Conditions';
    return value.map(formatConditionLine).join(' AND ');
  }
  if (value == null || value === '') return '—';
  if (key === 'requirement') return getRequirementLabel(value);
  if (key === 'limitFor') return getLimitForLabel(value);
  if (key === 'limitType') return getLimitTypeLabel(value);
  if (key === 'operator') return getOperatorLabel(value);
  if (key === 'unit') return getUnitLabel(value);
  if (key === 'minimumBoundary' || key === 'maximumBoundary') {
    return value === 'included' ? 'Included' : value === 'excluded' ? 'Excluded' : String(value);
  }
  return String(value);
}

export function diffRuleSnapshots(prev, next) {
  const changes = [];
  TRACKED_FIELDS.forEach((key) => {
    const oldDisplay = displayFieldValue(key, prev?.[key]);
    const newDisplay = displayFieldValue(key, next?.[key]);
    if (oldDisplay !== newDisplay) {
      changes.push({ field: fieldLabel(key), previousValue: oldDisplay, newValue: newDisplay });
    }
  });
  return changes;
}

function snapshotsEqual(a, b) {
  return diffRuleSnapshots(a, b).length === 0;
}

export function appendConfigurationHistory(rule, action, changes, status, version, changeReason = '') {
  const ts = new Date().toISOString();
  const actor = limitRuleActor();
  const base = Array.isArray(rule.configurationHistory) ? rule.configurationHistory : [];
  const changeRows = changes.length ? changes : [{ field: '—', previousValue: '—', newValue: '—' }];
  const entries = changeRows.map((c) => ({
    ruleId: rule.id,
    version,
    action,
    field: c.field,
    previousValue: c.previousValue,
    newValue: c.newValue,
    status,
    actor: actor.name,
    role: actor.role,
    accountId: actor.accountId,
    timestamp: ts,
    changeReason,
  }));
  try {
    emitConfigurationHistoryBatch({
      rows: entries,
      module: 'Configuration — Limits & Thresholds',
      sourceModuleRef: 'configuration_limits',
      changedItem: rule.ruleName || rule.id,
      itemId: rule.id,
      actor: { name: actor.name, role: actor.role },
      stamp: ts,
      changeType: action === 'Add' || action === 'Created' ? 'Add' : 'Edit',
      changeReason,
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  return [...base, ...entries];
}

export function appendTestHistory(rule, summary, passed) {
  const actor = limitRuleActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const isNew = !publishedBaseline?.id;
  const actor = limitRuleActor();
  const nowIso = payload.lastUpdated || new Date().toISOString();

  if (!isNew) {
    const prevSnap = ruleSnapshotFromForm(publishedBaseline);
    if (snapshotsEqual(prevSnap, snapshot)) {
      return {
        rules: allRules,
        record: getPublishedRule(publishedBaseline),
        unchanged: true,
        isNew: false,
      };
    }
  }

  const id = isNew ? payload.id || nextLimitRuleId(allRules) : publishedBaseline.id;
  const changes = isNew
    ? diffRuleSnapshots({}, snapshot)
    : diffRuleSnapshots(ruleSnapshotFromForm(publishedBaseline), snapshot);

  const record = {
    ...(isNew ? {} : getPublishedRule(publishedBaseline)),
    ...snapshot,
    id,
    version: isNew ? 1 : publishedBaseline.version || 1,
    pendingDraft: null,
    configurationHistory: appendConfigurationHistory(
      {
        id,
        ruleName: snapshot.ruleName,
        configurationHistory: publishedBaseline?.configurationHistory || [],
      },
      isNew ? 'Add' : 'Edit',
      changes,
      'saved',
      isNew ? 1 : publishedBaseline.version || 1,
      snapshot.changeReason,
    ),
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || publishedBaseline.createdOn || nowIso,
    addedBy: isNew ? actor.name : publishedBaseline.addedBy || publishedBaseline.createdBy || actor.name,
    lastUpdated: nowIso,
    lastUpdatedBy: actor.name,
  };

  const rules = isNew
    ? [...allRules.filter((r) => r.id !== record.id), record]
    : allRules.map((r) => (r.id === record.id ? record : r));

  return { rules, record, unchanged: false, isNew };
}

export function saveLimitRule(allRules, payload, publishedBaseline, options = {}) {
  const excludeId = publishedBaseline?.id || payload?.id || undefined;
  const validation = collectLimitSaveErrors(payload, allRules, excludeId, {
    originalLastUpdated:
      options.originalLastUpdated
      ?? publishedBaseline?.lastUpdated
      ?? publishedBaseline?.modifiedOn,
    originalScopeKey:
      options.originalScopeKey
      ?? (publishedBaseline ? buildLimitScopeKey(publishedBaseline) : undefined),
    canSave: options.canSave !== false,
    checksAvailable: options.checksAvailable !== false,
  });

  if (validation.summary.length) {
    return {
      success: false,
      errors: validation.errors,
      summary: validation.summary,
      rules: allRules,
      unchanged: false,
    };
  }

  try {
    const merged = mergeRulesAfterSave(allRules, payload, publishedBaseline || {});
    return {
      success: true,
      errors: {},
      summary: [],
      rules: merged.rules,
      record: merged.record,
      unchanged: Boolean(merged.unchanged),
      isNew: Boolean(merged.isNew),
    };
  } catch {
    return {
      success: false,
      errors: { form: 'The Rule could not be saved. Your changes have been kept for review.' },
      summary: ['The Rule could not be saved. Your changes have been kept for review.'],
      rules: allRules,
      unchanged: false,
    };
  }
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  const result = saveLimitRule(
    allRules,
    { ...payload, changeReason: payload.changeReason || 'Save from configuration test' },
    publishedBaseline || {},
    { canSave: true, checksAvailable: true },
  );
  return result.rules;
}

export function collectActivationErrors(formData, allRules, excludeId) {
  return collectLimitSaveErrors(formData, allRules, excludeId).summary;
}

export function runLimitRuleTest(formData, allRules) {
  const snapshot = ruleSnapshotFromForm(formData);
  const validation = collectLimitSaveErrors(formData, allRules, formData.id || undefined);
  const lines = [
    `Requirement: ${getRequirementLabel(snapshot.requirement) || '—'}`,
    `Limit For: ${getLimitForLabel(snapshot.limitFor) || '—'}`,
    `Purpose meaning: ${getPurposeMeaning(snapshot.limitFor) || '—'}`,
    `Purpose reference: ${getPurposeReference(snapshot.limitFor) || '—'}`,
    `Measure: ${getMeasureLabel(snapshot.requirement) || '—'}`,
    `Limit Type: ${getLimitTypeLabel(snapshot.limitType) || '—'}`,
    `Operator: ${getOperatorLabel(snapshot.operator) || '—'}`,
    `Limit: ${formatLimitDisplay(snapshot)}`,
    `Unit: ${getUnitLabel(snapshot.unit) || '—'}`,
  ];
  if ((snapshot.conditions || []).length) {
    lines.push('', 'Conditions:', ...snapshot.conditions.map((condition) => `• ${formatConditionLine(condition)}`));
  } else {
    lines.push('', 'No Conditions');
  }
  if (validation.summary.length) {
    return {
      passed: false,
      summary: 'Rule test blocked. Configuration conflicts or missing requirements were found.',
      detail: [...lines, '', 'Issues:', ...validation.summary.map((message) => `• ${message}`)].join('\n'),
    };
  }
  return {
    passed: true,
    summary: 'Rule test passed. Configuration is valid — no live measurement was changed.',
    detail: [...lines, '', 'No duplicate, conflicting or overlapping Rules detected.'].join('\n'),
  };
}

export function mergeRulesAfterSaveDraft(allRules, payload, publishedBaseline) {
  return mergeRulesAfterActivate(allRules, payload, publishedBaseline);
}

export function mergeRulesAfterInactivate(allRules) {
  return allRules;
}

export function mergeRulesAfterListActivate(allRules) {
  return allRules;
}

export { normalizeLimitForm, buildLimitScopeKey };
